import { useEffect } from 'react';
import { useUserData } from 'flows/core/hooks/use-profile';
import {
  useGetEstimatedActivationDate,
  type ActivationDefaults,
} from 'flows/tvi/hooks/use-get-estimated-activation-date';

import { Address, CreditCard, PiiTokenizedData } from 'flows/tvi/types';
import { createFormStore } from 'flows/tvi/utils/buy/config/common/form-store';
import {
  HomeServicesReservation,
  InstallationOptionsEnum,
} from '@updater/consumer-graph';
import { SMS_OPT_IN_VALUE } from 'flows/tvi/components/review/utils/constants';
import {
  Reservation,
  useBuyFlowContext,
} from 'flows/tvi/generic-buyflow/contexts/BuyFlowContext';

export type ATTFormData = {
  internetModemChoice?: string;
  ssn: string | PiiTokenizedData;
  dob: string | PiiTokenizedData;
  passcode?: string | PiiTokenizedData;
  securityQuestion?: string;
  securityAnswer?: string | PiiTokenizedData;
  date1?: string;
  date1InstallationWindow?: Array<string>;
  date2?: string;
  date2InstallationWindow?: Array<string>;
  date3?: string;
  date3InstallationWindow?: Array<string>;
  prescreenOptOut?: boolean;
  flexDevices?: string;
  creditCheckMethod?:
    | 'autopay'
    | 'manual'
    | 'noAutopay'
    | 'autopayCredit'
    | 'autopayDebit';
  autopayTermsAgreement?: boolean;
  paperlessBillingOptIn?: boolean;
  email: string;
  firstName: string;
  lastName: string;
  phone: string;
  smsOptIn: 'sms'[];
  paperlessBilling?: boolean;
  reviewAutopayTermsAgreement?: boolean;
  currentAddress?: Address;
  creditCard?: Partial<CreditCard>;
  shippingAddress?: string;
  newShippingAddress?: Address;
  /*
  This field is not UI visible
  It is used by informant to conditionally render the `Schedule Installation` Screen
  based if the user has self install enabled or not
*/
  installationType: InstallationOptionsEnum;
};

export enum AttPages {
  MODEM_CHOICE = 'modems',
  CUSTOMER_INFO = 'customer-info',
  IDENTITY = 'identity',
  SECURE_ACCOUNT = 'secure-account',
  SCHEDULE_INSTALLATION = 'schedule-installation',
  PAYMENT_OPTION = 'payment-option',
  PAYMENT = 'payment-method',
  REVIEW = 'buy/review',
  SUBMISSION = 'submission',
  ERROR = 'error',
}

export const omitKeysForLogging = [
  'dob',
  'ssn',
  'passcode',
  'securityQuestion',
  'securityAnswer',
  'creditCard',
];

export type UseStoreWrapper = typeof useStore;
const partialFormDataFromReservation = (
  reservation: HomeServicesReservation | Reservation,
  activationDefaults: ActivationDefaults
) => {
  const installationTypeFromReservation =
    reservation?.offer?.options?.installationOptions?.type;

  // Convert hook's string format to array for AT&T form data
  const defaultInstallWindowTime = activationDefaults.defaultTimeWindow
    ? [activationDefaults.defaultTimeWindow]
    : undefined;

  return {
    installationType:
      (installationTypeFromReservation as InstallationOptionsEnum) ??
      InstallationOptionsEnum.self,
    date1InstallationWindow: defaultInstallWindowTime,
  };
};

const useStore = createFormStore<ATTFormData, AttPages>({
  provider: 'att',
  initialPageRoute: AttPages.IDENTITY,
  initialFormData: {},
});

export function getATTFormState() {
  return useStore.getState();
}

export const useATTStore = (...args: Parameters<typeof useStore>) => {
  const status = useStore((state) => state.status);
  const formData = useStore((state) => state.formData);
  const initializeFormData = useStore((state) => state.initializeFormData);
  const resetFormData = useStore((state) => state.resetFormData);
  const lastIdentifier = useStore((state) => state.lastIdentifier);

  // Get context for reservation and customer info (works for both guest and auth modes)
  const { reservation, isGuestMode, customerInfo, installAddress, identifier } =
    useBuyFlowContext();

  const { loading: userDataLoading, user } = useUserData();
  const activationDefaults = useGetEstimatedActivationDate();

  const hasHydrated = useStore.persist?.hasHydrated?.();

  const installationTypeFromReservation =
    reservation?.offer?.options?.installationOptions?.type;

  // Reset form when identifier changes (new session detected)
  useEffect(() => {
    if (
      hasHydrated &&
      identifier &&
      lastIdentifier &&
      identifier !== lastIdentifier
    ) {
      resetFormData();
    }
  }, [hasHydrated, identifier, lastIdentifier, resetFormData]);

  useEffect(() => {
    // Different loading conditions for guest vs auth mode
    if (
      hasHydrated &&
      status !== 'loaded' &&
      !userDataLoading &&
      reservation &&
      !activationDefaults.loading
    ) {
      // Guest mode: Get customer info from context
      // Auth mode: Get customer info from user object
      let customerFirstName: string | undefined;
      let customerLastName: string | undefined;
      let customerEmail: string | undefined;
      let customerPhone: string | undefined;

      if (isGuestMode && customerInfo) {
        customerFirstName = customerInfo.firstName;
        customerLastName = customerInfo.lastName;
        customerEmail = customerInfo.email;
        customerPhone = customerInfo.phone;
      } else {
        customerFirstName = user?.firstName ?? undefined;
        customerLastName = user?.lastName ?? undefined;
        customerEmail = user?.email ?? undefined;
        customerPhone = user?.phone ?? undefined;
      }

      // Guest mode always uses self-install
      const installationType = isGuestMode
        ? InstallationOptionsEnum.self
        : (installationTypeFromReservation as InstallationOptionsEnum);

      const initialData: Partial<ATTFormData> = {
        ...partialFormDataFromReservation(reservation!, activationDefaults),
        installationType,
        firstName: customerFirstName,
        lastName: customerLastName,
        email: customerEmail,
        phone: customerPhone,
        smsOptIn: [SMS_OPT_IN_VALUE],
        prescreenOptOut: false,
        paperlessBilling: true,
        // Only include auth-specific fields when not in guest mode
        ...(!isGuestMode && {
          date1: activationDefaults.estimatedDate ?? '',
          date2: '',
          date3: '',
          creditCheckMethod: 'autopay',
          flexDevices: 'one',
          creditCard: {
            firstName: customerFirstName,
            lastName: customerLastName,
            address: <Address>{
              street: user?.currentMove?.fromAddress?.street ?? undefined,
              unit: user?.currentMove?.fromAddress?.unit ?? '',
              city: user?.currentMove?.fromAddress?.city ?? undefined,
              state: user?.currentMove?.fromAddress?.state ?? undefined,
              postalCode:
                user?.currentMove?.fromAddress?.postalCode ?? undefined,
            },
          },
        }),
      };

      initializeFormData({
        formData: initialData,
        offerName: reservation?.offer?.name,
        identifier,
      });
    }
  }, [
    hasHydrated,
    status,
    formData?.installationType,
    userDataLoading,
    user,
    initializeFormData,
    installationTypeFromReservation,
    reservation,
    activationDefaults,
    isGuestMode,
    customerInfo,
    installAddress,
    identifier,
  ]);

  return useStore(...args);
};
